import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  House,
  Buildings,
  Users,
  Megaphone,
  Globe,
  Layout,
  FileText,
  File,
  ChartBar,
  Gear,
  ClockCounterClockwise,
  Images,
  SignOut,
  List,
  X,
  CaretDown,
  UserCircle
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const menuItems = [
  { path: '/dashboard', label: 'Übersicht', icon: House },
  { path: '/departments', label: 'Abteilungen', icon: Buildings, roles: ['super_admin', 'org_admin'] },
  { path: '/users', label: 'Benutzer', icon: Users, roles: ['super_admin', 'org_admin', 'dept_admin'] },
  { path: '/campaigns', label: 'Kampagnen', icon: Megaphone },
  { path: '/domains', label: 'Domains', icon: Globe, roles: ['super_admin'] },
  { path: '/templates', label: 'Template-Designer', icon: Layout },
  { path: '/submissions', label: 'Einreichungen', icon: FileText },
  { path: '/tickets', label: 'Tickets', icon: File },
  { path: '/analytics', label: 'Analysen', icon: ChartBar },
  { path: '/audit-logs', label: 'Audit-Logs', icon: ClockCounterClockwise, roles: ['super_admin', 'org_admin', 'dept_admin'] },
  { path: '/media', label: 'Medienbibliothek', icon: Images },
  { path: '/settings', label: 'Einstellungen', icon: Gear, roles: ['super_admin'] },
];

export function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const filteredMenuItems = menuItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role);
  });

  const getRoleBadge = (role) => {
    const roleLabels = {
      super_admin: 'Super Admin',
      org_admin: 'Organisations-Admin',
      dept_admin: 'Abteilungs-Admin',
      campaign_manager: 'Kampagnenmanager',
      editor: 'Editor',
      viewer: 'Viewer',
      data_analyst: 'Datenanalyst'
    };
    return roleLabels[role] || role;
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Mobile Header */}
      <div className="lg:hidden header-glass px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setSidebarOpen(true)}
          className="p-2 hover:bg-zinc-100 rounded-sm"
          data-testid="mobile-menu-toggle"
        >
          <List className="w-5 h-5" />
        </button>
        <span className="font-bold text-lg tracking-tight">Kampagnenplattform</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-2 hover:bg-zinc-100 rounded-sm" data-testid="mobile-user-menu">
              <UserCircle className="w-5 h-5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleLogout}>
              <SignOut className="w-4 h-4 mr-2" />
              Abmelden
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white border-r border-zinc-200 p-4">
            <div className="flex items-center justify-between mb-6">
              <span className="font-bold text-lg">Menü</span>
              <button onClick={() => setSidebarOpen(false)} className="p-2 hover:bg-zinc-100 rounded-sm">
                <X className="w-5 h-5" />
              </button>
            </div>
            <nav className="space-y-1">
              {filteredMenuItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`sidebar-item ${isActive ? 'active' : ''}`}
                    data-testid={`nav-${item.path.slice(1)}`}
                  >
                    <Icon className="w-5 h-5" weight={isActive ? 'fill' : 'regular'} />
                    {item.label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:min-h-screen bg-white border-r border-zinc-200">
          <div className="p-6 border-b border-zinc-200">
            <h1 className="text-xl font-black tracking-tight text-zinc-900">Kampagnenplattform</h1>
            <p className="text-xs text-zinc-500 mt-1">Verwaltung &amp; Steuerung</p>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {filteredMenuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`sidebar-item ${isActive ? 'active' : ''}`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="w-5 h-5" weight={isActive ? 'fill' : 'regular'} />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-zinc-200">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="w-full flex items-center gap-3 p-3 rounded-sm hover:bg-zinc-100 transition-colors"
                  data-testid="user-menu-trigger"
                >
                  <div className="w-9 h-9 rounded-sm bg-[#002FA7] flex items-center justify-center text-white font-semibold">
                    {user?.name?.charAt(0) || 'U'}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-zinc-900 truncate">{user?.name}</p>
                    <p className="text-xs text-zinc-500">{getRoleBadge(user?.role)}</p>
                  </div>
                  <CaretDown className="w-4 h-4 text-zinc-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-button">
                  <SignOut className="w-4 h-4 mr-2" />
                  Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
}
